import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { GuideTypes, IGuide, KeyCodeTypes } from 'common/interfaces';
import useAddEventListener from 'hooks/useAddEventListener';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { useCallback } from 'react';
import { DragAndDropService } from 'services/DragAndDropService';
import KeyboardService from 'services/KeyboardService';
import {
  moveSelectedGuides,
  updateGuideProps,
} from 'store/reducers/guides.slice';

export default function useGuideDragAndDrop(
  guideData: IGuide,
  selectedGuidesIds: string[],
  elemRef: React.RefObject<HTMLDivElement>,
  pressedKeys: string[],
  dispatch: Dispatch<AnyAction>,
): void {
  const stickyPoints = useTypedSelector((state) => state.guides.stickyPoints);

  const dragAndDropHandler = useCallback(
    (event: MouseEvent): void => {
      event.preventDefault();

      if (guideData.isBlocked) return;

      const domElem = elemRef.current;
      const target = event.target as HTMLElement;

      if (
        domElem &&
        !KeyboardService.checkIfPressedOneKeyOf(pressedKeys, [
          KeyCodeTypes.SPACE,
          KeyCodeTypes.SHIFT_LEFT,
          KeyCodeTypes.SHIFT_RIGHT,
        ]) &&
        target.hasAttribute('data-guide-drag-target')
      ) {
        const isMultipleGuidesSelected = selectedGuidesIds.length > 1;

        const dragAndDropService = new DragAndDropService(
          guideData.id,
          domElem,
          event,
          pressedKeys,
          stickyPoints,
          dispatch,
        );

        const changeStateCallback = (
          xOffset: number,
          yOffset: number,
        ): void => {
          dispatch(
            moveSelectedGuides({
              xOffset: guideData.type === GuideTypes.VERTICAL ? xOffset : 0,
              yOffset: guideData.type === GuideTypes.HORIZONTAL ? yOffset : 0,
            }),
          );
        };

        const moveHandler = isMultipleGuidesSelected
          ? dragAndDropService.getMoveSelectedGuidesHandler(changeStateCallback)
          : dragAndDropService.getMoveGuideHandler(guideData);

        const onMouseMove = (event: MouseEvent): void => {
          moveHandler(event.pageX, event.pageY);
        };

        const onMouseUp = (): void => {
          if (!isMultipleGuidesSelected) {
            const updatedProps =
              guideData.type === GuideTypes.HORIZONTAL
                ? { y: parseInt(domElem.style.top) }
                : { x: parseInt(domElem.style.left) };

            dispatch(
              updateGuideProps({
                id: guideData.id,
                updatedProps,
              }),
            );
          }
        };

        dragAndDropService.addListenersWithFinishCallback(
          onMouseMove,
          onMouseUp,
        );
      }
    },
    [
      guideData,
      dispatch,
      pressedKeys,
      elemRef,
      selectedGuidesIds,
      stickyPoints,
    ],
  );

  useAddEventListener(elemRef, 'mousedown', dragAndDropHandler);
}
